import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Store,
  Loader2,
  Search,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Star,
  X,
  Upload,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Circle,
  Lock,
} from "lucide-react";
import {
  fetchAdminListings,
  createAdminListing,
  updateAdminListing,
  deleteAdminListing,
  uploadAdminListingPhoto,
} from "@/api/adminListings";
import { CATEGORY_SLUGS, CATEGORY_LABELS_I18N } from "@/api/listings";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const CATEGORY_OPTIONS = CATEGORY_SLUGS.map((slug) => ({
  value: slug,
  label: CATEGORY_LABELS_I18N[slug]?.nl || slug,
}));

/**
 * Bereken volledigheid van een listing (zelfde logica als de publieke sortering):
 *  - "complete"  = eigen omschrijving EN foto
 *  - "partial"   = omschrijving OF foto (één van beide)
 *  - "basic"     = geen van beide
 */
const listingCompleteness = (item) => {
  const hasDesc = !!(item?.description_nl && String(item.description_nl).trim());
  const hasImage = !!(item?.image && String(item.image).trim());
  if (hasDesc && hasImage) return "complete";
  if (hasDesc || hasImage) return "partial";
  return "basic";
};

const COMPLETENESS_META = {
  complete: {
    label: "Compleet",
    dotClass: "bg-[#2A7A3F]",
    ringClass: "ring-[#2A7A3F]/25",
    hint: "Eigen omschrijving + foto",
  },
  partial: {
    label: "Deels",
    dotClass: "bg-[#F5A623]",
    ringClass: "ring-[#F5A623]/25",
    hint: "Omschrijving OF foto ontbreekt",
  },
  basic: {
    label: "Basis",
    dotClass: "bg-[#8AA3B8]",
    ringClass: "ring-[#8AA3B8]/25",
    hint: "Geen eigen omschrijving of foto",
  },
};

const emptyForm = {
  name: "",
  category: "winkelen",
  soort: "",
  membership: "",
  description_nl: "",
  phone: "",
  email: "",
  website: "",
  address: "",
  postcode: "",
  city: "Lemmer",
  opening_hours: "",
  image: "",
  published: true,
  featured: false,
};

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-xs font-[Poppins] font-semibold uppercase tracking-wide text-[#5A7594] block mb-1.5">
      {label}
    </span>
    {children}
    {hint && <span className="text-xs text-[#8AA3B8] block mt-1">{hint}</span>}
  </label>
);

const Toast = ({ toast }) =>
  toast ? (
    <div
      data-testid="admin-toast"
      className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg font-[Poppins] font-semibold text-sm flex items-center gap-2 ${
        toast.type === "error"
          ? "bg-[#8b1420] text-white"
          : "bg-[#173A5E] text-white"
      }`}
    >
      {toast.type === "error" ? (
        <AlertCircle className="w-4 h-4" />
      ) : (
        <CheckCircle2 className="w-4 h-4" />
      )}
      {toast.message}
    </div>
  ) : null;

const ListingFormModal = ({ initial, onClose, onSaved, showToast }) => {
  const isNew = !initial;
  const [form, setForm] = useState(() => {
    const base = { ...emptyForm, ...(initial || {}) };
    // Normaliseer: als initial.category maar geen categories heeft, converteer.
    if (!Array.isArray(base.categories) || base.categories.length === 0) {
      base.categories = initial?.category ? [initial.category] : ["winkelen"];
    }
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCategory = (slug) =>
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(slug)
        ? f.categories.filter((c) => c !== slug)
        : [...f.categories, slug],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast("Naam is verplicht", "error");
      return;
    }
    if (!form.categories?.length) {
      showToast("Kies minstens één categorie", "error");
      return;
    }
    setSaving(true);
    try {
      // Stuur zowel categories[] als legacy category (eerste in de lijst) mee
      const payload = { ...form, category: form.categories[0] };
      if (isNew) {
        const created = await createAdminListing(payload);
        showToast(`"${created.name}" aangemaakt`);
        onSaved(created);
      } else {
        const res = await updateAdminListing(initial.slug, payload);
        showToast(`"${res.listing.name}" bijgewerkt`);
        onSaved(res.listing);
      }
    } catch (err) {
      showToast(err?.response?.data?.detail || "Opslaan mislukt", "error");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || isNew) {
      if (isNew) showToast("Sla eerst het bedrijf op, upload dan de foto.", "error");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAdminListingPhoto(initial.slug, file);
      set("image", res.image);
      showToast("Foto geüpload");
    } catch (err) {
      showToast(err?.response?.data?.detail || "Upload mislukt", "error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const preview = form.image
    ? form.image.startsWith("http")
      ? form.image
      : form.image.startsWith("/api/")
      ? `${BACKEND_URL}${form.image}`
      : form.image
    : "";

  return (
    <div
      data-testid="listing-form-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl my-8"
      >
        <div className="sticky top-0 bg-white border-b border-[#E4EEF5] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-[#173A5E] font-[Poppins]">
            {isNew ? "Nieuw bedrijf" : `Bewerken: ${initial.name}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="listing-form-close"
            className="p-2 rounded-lg hover:bg-[#F4F8FB]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Naam *">
            <input
              type="text"
              required
              data-testid="form-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none"
            />
          </Field>
          <Field label="Categorieën * (meerkeuze)">
            <div
              data-testid="form-categories"
              className="grid grid-cols-2 gap-2 p-3 border border-[#E4EEF5] rounded-lg bg-[#F9FBFD]"
            >
              {CATEGORY_OPTIONS.map((o) => (
                <label
                  key={o.value}
                  className="inline-flex items-center gap-2 text-sm cursor-pointer hover:text-[#173A5E]"
                >
                  <input
                    type="checkbox"
                    checked={form.categories.includes(o.value)}
                    onChange={() => toggleCategory(o.value)}
                    data-testid={`form-cat-${o.value}`}
                    className="w-4 h-4 accent-[#2E9CD6]"
                  />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          </Field>
          <Field label="Soort" hint="Bijv. Restaurant, Kledingwinkel, Kapper…">
            <input
              type="text"
              data-testid="form-soort"
              value={form.soort}
              onChange={(e) => set("soort", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <Field label="Lidmaatschap" hint="Bijv. BIZ-lid">
            <input
              type="text"
              data-testid="form-membership"
              value={form.membership}
              onChange={(e) => set("membership", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Omschrijving (NL)">
              <textarea
                data-testid="form-description"
                rows={5}
                value={form.description_nl}
                onChange={(e) => set("description_nl", e.target.value)}
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 font-[Inter]"
              />
            </Field>
          </div>
          <Field label="Adres">
            <input
              type="text"
              data-testid="form-address"
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Postcode">
              <input
                type="text"
                data-testid="form-postcode"
                value={form.postcode}
                onChange={(e) => set("postcode", e.target.value)}
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
              />
            </Field>
            <Field label="Plaats">
              <input
                type="text"
                data-testid="form-city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
              />
            </Field>
          </div>
          <Field label="Telefoon">
            <input
              type="text"
              data-testid="form-phone"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <Field label="E-mail">
            <input
              type="email"
              data-testid="form-email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Website">
              <input
                type="url"
                data-testid="form-website"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                placeholder="https://…"
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Openingstijden">
              <input
                type="text"
                data-testid="form-opening"
                value={form.opening_hours}
                onChange={(e) => set("opening_hours", e.target.value)}
                placeholder="ma-za 9.00-17.30"
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Foto" hint={isNew ? "Sla eerst op, upload daarna een foto." : "JPG/PNG/WEBP — vervangt bestaande foto."}>
              <div className="flex items-start gap-4 flex-wrap">
                {preview && (
                  <img
                    src={preview}
                    alt="preview"
                    data-testid="form-image-preview"
                    className="w-32 h-32 object-cover rounded-lg border border-[#E4EEF5]"
                  />
                )}
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onPhoto}
                    disabled={isNew || uploading}
                    data-testid="form-image-file"
                    className="text-sm"
                  />
                  <input
                    type="text"
                    value={form.image}
                    onChange={(e) => set("image", e.target.value)}
                    placeholder="of /media/… of https://…"
                    className="w-72 max-w-full border border-[#E4EEF5] rounded-lg px-3 py-2 text-xs"
                  />
                  {uploading && <span className="text-xs text-[#2E9CD6]">Bezig met uploaden…</span>}
                </div>
              </div>
            </Field>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-4 pt-2 border-t border-[#E4EEF5]">
            <label className="inline-flex items-center gap-2 text-sm text-[#173A5E] font-[Poppins] cursor-pointer">
              <input
                type="checkbox"
                data-testid="form-published"
                checked={form.published}
                onChange={(e) => set("published", e.target.checked)}
                className="w-4 h-4 accent-[#2E9CD6]"
              />
              Zichtbaar op de site
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-[#173A5E] font-[Poppins] cursor-pointer">
              <input
                type="checkbox"
                data-testid="form-featured"
                checked={form.featured}
                onChange={(e) => set("featured", e.target.checked)}
                className="w-4 h-4 accent-[#F5A623]"
              />
              Uitgelicht (bovenaan in de gids)
            </label>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E4EEF5] px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
          >
            Annuleren
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="form-save"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isNew ? "Aanmaken" : "Opslaan"}
          </button>
        </div>
      </form>
    </div>
  );
};

export const AdminListingsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [pubFilter, setPubFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "complete" | "partial" | "basic"
  const [modifiedFilter, setModifiedFilter] = useState(""); // "" | "modified" | "seed"
  const [editing, setEditing] = useState(null); // listing being edited (or "new")
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const showToast = useCallback((message, type = "ok") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (catFilter) params.category = catFilter;
      if (pubFilter === "published") params.published = true;
      if (pubFilter === "hidden") params.published = false;
      const data = await fetchAdminListings(params);
      setItems(data.items || []);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Ophalen mislukt", "error");
    } finally {
      setLoading(false);
    }
  }, [q, catFilter, pubFilter, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side filter op status (volledigheid) + admin_modified — status wordt afgeleid per rij.
  const visibleItems = useMemo(() => {
    let list = items;
    if (statusFilter) list = list.filter((it) => listingCompleteness(it) === statusFilter);
    if (modifiedFilter === "modified") list = list.filter((it) => it.admin_modified === true);
    if (modifiedFilter === "seed") list = list.filter((it) => !it.admin_modified);
    return list;
  }, [items, statusFilter, modifiedFilter]);

  const filteredCount = visibleItems.length;

  // Tellingen per status voor de filter-dropdown.
  const statusCounts = useMemo(() => {
    const c = { complete: 0, partial: 0, basic: 0 };
    for (const it of items) c[listingCompleteness(it)] += 1;
    return c;
  }, [items]);

  const modifiedCounts = useMemo(() => {
    let modified = 0;
    for (const it of items) if (it.admin_modified === true) modified += 1;
    return { modified, seed: items.length - modified };
  }, [items]);

  const togglePublished = async (item) => {
    try {
      const res = await updateAdminListing(item.slug, { ...item, published: !item.published });
      setItems((prev) => prev.map((p) => (p.slug === item.slug ? res.listing : p)));
      showToast(res.listing.published ? "Zichtbaar" : "Verborgen");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Wijzigen mislukt", "error");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      const res = await updateAdminListing(item.slug, { ...item, featured: !item.featured });
      setItems((prev) => prev.map((p) => (p.slug === item.slug ? res.listing : p)));
      showToast(res.listing.featured ? "Uitgelicht" : "Niet meer uitgelicht");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Wijzigen mislukt", "error");
    }
  };

  const doDelete = async (item) => {
    try {
      await deleteAdminListing(item.slug);
      setItems((prev) => prev.filter((p) => p.slug !== item.slug));
      showToast(`"${item.name}" verwijderd`);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Verwijderen mislukt", "error");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div data-testid="admin-listings-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#173A5E] font-[Poppins] flex items-center gap-2">
            <Store className="w-7 h-7 text-[#2E9CD6]" />
            Bedrijven
          </h1>
          <p className="text-sm text-[#5A7594] mt-1">
            Beheer alle bedrijven uit de gids. {filteredCount} zichtbaar in filter.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="btn-new-listing"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Nieuw bedrijf
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E4EEF5] p-4 mb-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md bg-[#F4F8FB] rounded-lg px-3 py-2 border border-transparent focus-within:border-[#6BC0E8]">
          <Search className="w-4 h-4 text-[#2E9CD6]" />
          <input
            data-testid="admin-listings-search"
            type="search"
            placeholder="Zoek op naam…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm text-[#173A5E]"
          />
        </div>
        <select
          data-testid="admin-listings-filter-category"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="border border-[#E4EEF5] rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Alle categorieën</option>
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          data-testid="admin-listings-filter-status"
          value={pubFilter}
          onChange={(e) => setPubFilter(e.target.value)}
          className="border border-[#E4EEF5] rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Alle statussen</option>
          <option value="published">Alleen zichtbaar</option>
          <option value="hidden">Alleen verborgen</option>
        </select>
        <select
          data-testid="admin-listings-filter-completeness"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-[#E4EEF5] rounded-lg px-3 py-2 text-sm bg-white"
          title="Filter op volledigheid van het bedrijfsprofiel"
        >
          <option value="">Alle volledigheden ({items.length})</option>
          <option value="complete">Compleet ({statusCounts.complete})</option>
          <option value="partial">Deels ({statusCounts.partial})</option>
          <option value="basic">Basis ({statusCounts.basic})</option>
        </select>
        <select
          data-testid="admin-listings-filter-modified"
          value={modifiedFilter}
          onChange={(e) => setModifiedFilter(e.target.value)}
          className="border border-[#E4EEF5] rounded-lg px-3 py-2 text-sm bg-white"
          title="Filter op handmatig aangepaste bedrijven"
        >
          <option value="">Alles ({items.length})</option>
          <option value="modified">
            Handmatig aangepast ({modifiedCounts.modified})
          </option>
          <option value="seed">Uit seed ({modifiedCounts.seed})</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#2E9CD6]" />
        </div>
      ) : visibleItems.length === 0 ? (
        <p className="text-[#5A7594] py-16 text-center">Geen bedrijven gevonden.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E4EEF5] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F8FB] text-[#173A5E] font-[Poppins]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold w-10">
                    <span title="Volledigheid van profiel (Compleet / Deels / Basis)">
                      <span className="sr-only">Volledigheid</span>
                      <Circle className="w-3.5 h-3.5 text-[#8AA3B8] inline" />
                    </span>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold">Naam</th>
                  <th className="text-left px-4 py-3 font-semibold">Categorie</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Uitgelicht</th>
                  <th className="text-right px-4 py-3 font-semibold">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4EEF5] font-[Inter]">
                {visibleItems.map((it) => {
                  const cs = listingCompleteness(it);
                  const meta = COMPLETENESS_META[cs];
                  return (
                  <tr key={it.slug} data-testid={`row-${it.slug}`} className="hover:bg-[#F9FBFD]">
                    <td className="px-4 py-3">
                      <span
                        data-testid={`completeness-${it.slug}`}
                        data-status={cs}
                        title={`${meta.label} — ${meta.hint}`}
                        aria-label={`Volledigheid: ${meta.label}`}
                        className="inline-flex items-center gap-2"
                      >
                        <span
                          className={`inline-block w-3 h-3 rounded-full ring-4 ${meta.dotClass} ${meta.ringClass}`}
                        />
                        <span className="sr-only">{meta.label}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="font-[Poppins] font-semibold text-[#173A5E]">
                          {it.name}
                        </div>
                        {it.admin_modified === true && (
                          <span
                            data-testid={`admin-modified-${it.slug}`}
                            title="Handmatig aangepast — wordt niet meer door de seed overschreven"
                            aria-label="Handmatig aangepast"
                            className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-[#EAF6FC] text-[#2E9CD6]"
                          >
                            <Lock className="w-3 h-3" strokeWidth={2.4} />
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#8AA3B8]">{it.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      {(it.categories && it.categories.length > 0
                        ? it.categories
                        : (it.category ? [it.category] : [])
                      )
                        .map((c) => CATEGORY_OPTIONS.find((o) => o.value === c)?.label || c)
                        .join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => togglePublished(it)}
                        data-testid={`toggle-published-${it.slug}`}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${
                          it.published
                            ? "bg-[#E6F5EA] text-[#2A7A3F]"
                            : "bg-[#FDECEE] text-[#8b1420]"
                        }`}
                      >
                        {it.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {it.published ? "Zichtbaar" : "Verborgen"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleFeatured(it)}
                        data-testid={`toggle-featured-${it.slug}`}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${
                          it.featured
                            ? "bg-[#FFF4D9] text-[#8B6914]"
                            : "bg-[#F4F8FB] text-[#5A7594]"
                        }`}
                      >
                        <Star className={`w-3 h-3 ${it.featured ? "fill-[#F5A623] text-[#F5A623]" : ""}`} />
                        {it.featured ? "Uitgelicht" : "Standaard"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={`/bedrijf/${it.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`view-${it.slug}`}
                          title="Bekijk publieke pagina"
                          className="p-2 rounded-lg text-[#2E9CD6] hover:bg-[#F4F8FB]"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => setEditing(it)}
                          data-testid={`edit-${it.slug}`}
                          className="p-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB]"
                          title="Bewerken"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(it)}
                          data-testid={`delete-${it.slug}`}
                          className="p-2 rounded-lg text-[#8b1420] hover:bg-[#FDECEE]"
                          title="Verwijderen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(creating || editing) && (
        <ListingFormModal
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved) => {
            setCreating(false);
            setEditing(null);
            load();
          }}
          showToast={showToast}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="confirm-delete-modal">
            <h3 className="text-xl font-bold text-[#173A5E] font-[Poppins] mb-2">Verwijderen bevestigen</h3>
            <p className="text-sm text-[#5A7594] mb-4">
              Weet je zeker dat je <strong>{confirmDelete.name}</strong> wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => doDelete(confirmDelete)}
                data-testid="confirm-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#8b1420] hover:bg-[#6a0f18] text-white font-[Poppins] font-semibold text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default AdminListingsPage;
